import {
  requestCodexRuntimeReadinessContinuation,
  type CodexRuntimeReadinessHttpResult,
} from "./codexRuntimeReadinessRequest";
import type { CodexReadinessBlockCode } from "./codexRuntimeReadiness";

type UnknownRecord = Record<string, unknown>;
type TerminalResult =
  | {
      readonly status: "ready";
      readonly accountEmail: string | null;
      readonly planType: string | null;
      readonly model: "gpt-5.6-sol";
      readonly credentialEpoch: number;
    }
  | { readonly status: "blocked"; readonly code: CodexReadinessBlockCode };

const BODY_KEYS = new Set(["ready", "code", "operation"]);
const OPERATION_KEYS = new Set(["runtimeId", "operationId", "status", "retryAt"]);
const STATUSES = new Set(["pending", "running", "retryable"]);
const MAX_ATTEMPTS = 60;
const MAX_POLL_DELAY_MS = 10_000;
const MAX_ELAPSED_MS = 60_000;

interface PendingOperation {
  readonly operationId: number;
  readonly retryAt: string | null;
}

function asRecord(value: unknown): UnknownRecord | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function hasExactKeys(value: UnknownRecord, keys: ReadonlySet<string>): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.size && actual.every((key) => keys.has(key));
}

function parsePendingOperation(value: unknown, runtimeId: string): PendingOperation | null {
  const operation = asRecord(value);
  if (!operation || !hasExactKeys(operation, OPERATION_KEYS)) return null;
  const operationId = operation.operationId;
  const valid = [
    operation.runtimeId === runtimeId,
    Number.isSafeInteger(operationId) && (operationId as number) > 0,
    typeof operation.status === "string" && STATUSES.has(operation.status),
    operation.retryAt === null ||
      (typeof operation.retryAt === "string" && Number.isFinite(Date.parse(operation.retryAt))),
  ];
  return valid.every(Boolean)
    ? { operationId: operationId as number, retryAt: operation.retryAt as string | null }
    : null;
}

function parseRetryAfter(value: string | null): number | null {
  const seconds = Number(value);
  return Number.isSafeInteger(seconds) && seconds >= 1 ? seconds * 1_000 : null;
}

function parseSameOriginContinuationUrl(input: {
  readonly location: string;
  readonly requestUrl: string;
  readonly runtimeId: string;
  readonly operationId: number;
}): string | null {
  const url = new URL(input.location, input.requestUrl);
  const expectedPath = `/api/cmmd/forge-runtimes/${encodeURIComponent(input.runtimeId)}/provider-readiness/codex/authority-upgrades/${input.operationId}`;
  const valid = [
    url.origin === new URL(input.requestUrl).origin,
    url.pathname === expectedPath,
    url.search === "",
    url.hash === "",
  ];
  return valid.every(Boolean) ? url.toString() : null;
}

function parsePendingBody(value: unknown, runtimeId: string): PendingOperation | null {
  const body = asRecord(value);
  if (!body) return null;
  const valid = [
    hasExactKeys(body, BODY_KEYS),
    body.ready === false,
    body.code === "RUNTIME_AUTHORITY_UPGRADE_PENDING",
  ];
  return valid.every(Boolean) ? parsePendingOperation(body.operation, runtimeId) : null;
}

function parseContinuation(
  response: CodexRuntimeReadinessHttpResult,
  runtimeId: string,
  expectedOperationId?: number,
): {
  readonly url: string;
  readonly retryAfterMs: number;
  readonly operationId: number;
} | null {
  if (response.status !== 202 || !response.structuredBody) return null;
  const operation = parsePendingBody(response.body, runtimeId);
  if (!operation || !response.location) return null;
  if (!operationIdentityMatches(operation.operationId, expectedOperationId)) return null;
  const url = parseSameOriginContinuationUrl({
    location: response.location,
    requestUrl: response.requestUrl,
    runtimeId,
    operationId: operation.operationId,
  });
  const retryAfterMs = retryDelay(response.retryAfter, operation.retryAt);
  if (!url || !retryAfterMs) return null;
  return {
    url,
    retryAfterMs,
    operationId: operation.operationId,
  };
}

function operationIdentityMatches(actual: number, expected: number | undefined): boolean {
  return expected === undefined || actual === expected;
}

function retryDelay(retryAfter: string | null, retryAt: string | null): number | null {
  const retryAfterMs = parseRetryAfter(retryAfter);
  if (!retryAfterMs) return null;
  const requiredDelayMs = retryAt
    ? Math.max(retryAfterMs, Math.max(0, Date.parse(retryAt) - Date.now()))
    : retryAfterMs;
  return requiredDelayMs <= MAX_POLL_DELAY_MS ? requiredDelayMs : null;
}

function wait(delayMs: number, signal: AbortSignal | undefined): Promise<void> {
  if (signal?.aborted) return Promise.reject(signal.reason);
  return new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      globalThis.clearTimeout(timeout);
      signal?.removeEventListener("abort", onAbort);
      reject(signal?.reason);
    };
    const timeout = globalThis.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export async function continueCodexRuntimeReadiness(input: {
  readonly initial: CodexRuntimeReadinessHttpResult;
  readonly runtimeId: string;
  readonly signal?: AbortSignal;
  readonly parseReady: (value: unknown, runtimeId: string) => TerminalResult | null;
  readonly parseBlocked: (value: unknown) => CodexReadinessBlockCode;
}): Promise<TerminalResult> {
  let response = input.initial;
  let operationId: number | undefined;
  const startedAt = Date.now();
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    if (response.status !== 202) {
      if (!response.ok) return { status: "blocked", code: input.parseBlocked(response.body) };
      return (
        input.parseReady(response.body, input.runtimeId) ?? {
          status: "blocked",
          code: "READINESS_RESPONSE_INVALID",
        }
      );
    }
    const continuation = parseContinuation(response, input.runtimeId, operationId);
    if (!continuation) return { status: "blocked", code: "READINESS_RESPONSE_INVALID" };
    operationId ??= continuation.operationId;
    const remainingMs = MAX_ELAPSED_MS - (Date.now() - startedAt);
    if (remainingMs <= 0) return { status: "blocked", code: "READINESS_UNAVAILABLE" };
    await wait(Math.min(continuation.retryAfterMs, remainingMs), input.signal);
    if (Date.now() - startedAt >= MAX_ELAPSED_MS) {
      return { status: "blocked", code: "READINESS_UNAVAILABLE" };
    }
    response = await requestCodexRuntimeReadinessContinuation({
      url: continuation.url,
      ...(input.signal ? { signal: input.signal } : {}),
    });
  }
  return { status: "blocked", code: "READINESS_UNAVAILABLE" };
}
